import React from "react";
import { BarCustomLayerProps, BarDatum } from "@nivo/bar";

interface CreateTrendLineLayerOptions {
  /** Trend value for each datum, indexed to match the chart data order. */
  trendValues: number[];
  color: string;
  isModal?: boolean;
}

/**
 * Dashed trend line drawn over the bars (e.g. a constant-CAGR reference line for
 * revenue projections). Points are placed at each bar's horizontal centre and at
 * the trend value's vertical position, so bars rising above the line read as
 * acceleration and bars below it as deceleration.
 */
export const createTrendLineLayer = ({
  trendValues,
  color,
  isModal = false,
}: CreateTrendLineLayerOptions) => {
  const dotRadius = isModal ? 4 : 3;

  const TrendLineLayer: React.FC<BarCustomLayerProps<BarDatum>> = ({ bars, yScale }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const toY = yScale as (value: number) => number;

    const points = bars
      .map((bar) => {
        const value = trendValues[bar.data.index];
        if (value == null || !Number.isFinite(value)) return null;
        return { x: bar.x + bar.width / 2, y: toY(value) };
      })
      .filter((point): point is { x: number; y: number } => point !== null)
      .sort((a, b) => a.x - b.x);

    if (points.length < 2) return null;

    const path = points
      .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
      .join(" ");

    return (
      <g>
        <path
          d={path}
          fill="none"
          stroke={color}
          strokeWidth={isModal ? 2.5 : 2}
          strokeDasharray={isModal ? "8 6" : "6 5"}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {points.map((point, index) => (
          <circle key={index} cx={point.x} cy={point.y} r={dotRadius} fill={color} />
        ))}
      </g>
    );
  };

  return TrendLineLayer;
};
